import { Badge, Group, Modal, ScrollArea, Stack, Table, Text } from '@mantine/core'

function resolveSubscorePointsEarned(subscore: { rating: number; maxPoints: number }): number | null {
  if (subscore.maxPoints <= 0) return null
  return (subscore.rating / 4) * subscore.maxPoints
}

interface EvaluationResultsModalProps {
  opened: boolean
  onClose: () => void
  evaluationData: {
    id: string
    runId: string
    overallScore: number
    judgeJustification: string | null
    subscores: Array<{
      criterion: string
      rating: number
      pointsDeducted: number
      maxPoints: number
      explanation?: string
    }>
    timestamp: string
    personaId?: string
    personaName?: string | null
    customerName?: string | null
  } | null
  loading: boolean
}

export function EvaluationResultsModal({
  opened,
  onClose,
  evaluationData,
  loading,
}: EvaluationResultsModalProps) {
  const formatPersonaName = (personaId?: string, personaName?: string | null): string => {
    if (personaName) {
      return personaName
    }
    if (personaId) {
      return personaId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }
    return 'Unknown Persona'
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="AI test results"
      size={800}
      styles={{
        body: {
          height: '600px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {loading ? (
        <Stack align="center" p="xl">
          <Text>Loading evaluation results...</Text>
        </Stack>
      ) : evaluationData ? (
        <ScrollArea style={{ flex: 1 }}>
          <Stack gap="md">
            {/* Overall Score */}
            <Group>
              <Text fw={600} size="lg">
                Overall Score:
              </Text>
              <Badge size="lg" color="blue" variant="light">
                {evaluationData.overallScore.toFixed(2)}/100
              </Badge>
            </Group>

            {/* Persona and Customer Info */}
            <Group gap="md">
              {(evaluationData.personaId || evaluationData.personaName) && (
                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    Persona:
                  </Text>
                  <Text size="sm" fw={500}>
                    {formatPersonaName(evaluationData.personaId, evaluationData.personaName)}
                  </Text>
                </div>
              )}
              {evaluationData.customerName && (
                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    Customer:
                  </Text>
                  <Text size="sm" fw={500}>
                    {evaluationData.customerName}
                  </Text>
                </div>
              )}
            </Group>

            {/* Judge Justification */}
            <div>
              <Text fw={600} size="sm" mb="xs">
                Judge Justification:
              </Text>
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                {evaluationData.judgeJustification || 'No justification provided'}
              </Text>
            </div>

            {/* Subscores Breakdown */}
            <div>
              <Text fw={600} size="sm" mb="xs">
                Subscores Breakdown:
              </Text>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Criterion</Table.Th>
                    <Table.Th>Rating</Table.Th>
                    <Table.Th>Points Earned</Table.Th>
                    <Table.Th>Max Points</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {evaluationData.subscores.map((subscore, index) => {
                    const earned = resolveSubscorePointsEarned(subscore)
                    return (
                      <Table.Tr key={index}>
                        <Table.Td>{subscore.criterion}</Table.Td>
                        <Table.Td>{subscore.rating}/4</Table.Td>
                        <Table.Td>{earned != null ? earned.toFixed(2) : '—'}</Table.Td>
                        <Table.Td>{subscore.maxPoints > 0 ? subscore.maxPoints : '—'}</Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>

              {/* Detailed Explanations */}
              <Stack gap="xs" mt="md">
                {evaluationData.subscores.map((subscore, index) => {
                  return (
                  <div key={index}>
                    <Text size="xs" fw={500}>
                      {subscore.criterion} (Rating: {subscore.rating}/4
                      {(() => {
                        const earned = resolveSubscorePointsEarned(subscore)
                        return earned != null && subscore.maxPoints > 0
                          ? `, Earned: ${earned.toFixed(2)}/${subscore.maxPoints}`
                          : ''
                      })()}
                      )
                    </Text>
                    {subscore.explanation && (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                        {subscore.explanation}
                      </Text>
                    )}
                  </div>
                  )
                })}
              </Stack>
            </div>

            {/* Timestamp */}
            <Group gap="md" mt="xs">
              <Text size="xs" c="dimmed">
                Evaluated: {new Date(evaluationData.timestamp).toLocaleString()}
              </Text>
            </Group>
          </Stack>
        </ScrollArea>
      ) : (
        <Text c="dimmed" ta="center">
          No evaluation results available for this conversation.
        </Text>
      )}
    </Modal>
  )
}

